import $ from 'jquery'
import store_laboratoire from '@/store/mods/analyses/laboratoire'
import EditAnalyse from '@/views/analyse/registre_analyse/edit-analyse'
import SuiviAnalyse from '@/components/laboratoire/suivi-analyse'
import SaisieAnalysePatient from './SaisieAnalysePatient.vue'
import { duree_lisible, date_heure_zone } from '@/utils/laboratoire-delais'

// LAB-005 : paillasse du laboratoire. LAB-009 : flux linéaire — onglet Technique (démarrer puis terminer chaque analyse,
// temps mesuré, vue Analyses ou Patients) puis onglet Saisie (analyses techniquées ; sans modèle de résultat, bouton pour
// le créer). L'enregistrement du résultat marque la ligne « résultat saisi » côté Clinique et l'envoie à la validation.
const message = (error, fallback) => error && error.response && error.response.data && typeof error.response.data.message === 'string' ? error.response.data.message : fallback

// Les tests unitaires montent parfois cette vue sans le mixin global. Le repli
// conserve strictement la même présentation M/F sans jamais modifier la donnée.
const sexeCourt = (view, value) => {
    if (typeof view.formatSexeCourt === 'function') return view.formatSexeCourt(value)
    const source = value && typeof value === 'object' ? (value.specificatif || value.description || value.libelle || '') : value
    const raw = source == null ? '' : String(source).trim()
    const normalise = raw.toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (['h', 'm', 'homme', 'masculin', 'masculine', 'male'].includes(normalise)) return 'M'
    if (['f', 'femme', 'feminin', 'feminine', 'female'].includes(normalise)) return 'F'
    return raw || '—'
}

export const ETATS = {
    attente: { nom: 'Technique' },
    a_saisir: { nom: 'Saisie' },
    saisis: { nom: 'Résultats saisis' }
}

export default {
    name: 'paillasse-analyses',
    components: { EditAnalyse, SuiviAnalyse, SaisieAnalysePatient },
    // LAB-UI-2 : integre = dans la carte du circuit ; etat = onglet imposé par la carte (attente | a_saisir | saisis), sans barre d'onglets propre
    props: { integre: { type: Boolean, default: false }, etat: { type: String, default: '' } },
    data() {
        return {
            onglet: 'attente',
            onglets: [{ id: 'attente', nom: 'Technique' }, { id: 'a_saisir', nom: 'Saisie' }, { id: 'saisis', nom: 'Résultats saisis' }],
            vue: 'analyses',   // LAB-009 : vue Analyses (une rangée par analyse) ou Patients (une rangée par bon)
            patient_modal: null, patient_fermeture_erreur: '',
            patient_enregistrement: false, patient_enregistrement_message: '', patient_enregistrement_erreur: false,
            patient_sortie_visible: false, patient_sortie_forcee: false, patient_sortie_destination: null,
            renseignement_visible: false, renseignement_patient: null, renseignement_retour: null,
            confirmation_visible: false, confirmation_technique: null, confirmation_retour: null,
            confirmation_en_cours: false, confirmation_erreur: '',
            recherche: '', page: 1, pages: 1, total: 0,
            lignes: [], details_ouverts: {}, compteurs: null, is_loading: false, erreur: '', info: '', sequence: 0, detruit: false, search_timer: null,
            en_action: [], maintenant: Date.now(), horloge: null,   // LAB-009 : lignes en cours de démarrage / fin, horloge du temps écoulé
            sans_modele: null,   // LAB-009 : ligne dont l'analyse n'a aucun modèle de résultat
            suivi_demande: null, suivi_toggler: 0,   // LAB-009 : parcours tracé du bon
            // Saisie : même mécanique que le registre d'analyses (fenêtre EditAnalyse, modèles du magasin)
            demande_id: 0, selected_analyse: '', has_model: false, add_result_loading: false, result_meta_data: {}, ligne_ouverte: null
        }
    },

    watch: {
        // La même instance Vue est réutilisée entre Technique et Saisie dans la carte du circuit.
        etat(nouveau) {
            const suivant = ETATS[nouveau] ? nouveau : 'attente'
            if (this.onglet === suivant) return
            this.onglet = suivant
            if (suivant === 'a_saisir') this.vue = 'patients'
            else this.vue = 'analyses'
            this.page = 1
            this.charger()
        }
    },

    computed: {
        current_modele_analyse() {
            return this.$store && this.$store.getters ? this.$store.getters.current_modele_analyse : null
        },

        modeles_analyses() {
            const modeles = this.$store && this.$store.getters ? this.$store.getters.modeles_analyses : []
            return Array.isArray(modeles) ? modeles : []
        },

        // LAB-009 : rangées de l'onglet Technique — une par analyse, ou une par patient (bon) regroupant ses analyses
        rangees() {
            if (this.vue !== 'patients') return this.lignes.map(ligne => ({ key: 'a-' + ligne.demande_id, ligne: ligne, lignes: [ligne], patient: false }))
            const groupes = []
            this.lignes.forEach((ligne) => {
                let groupe = groupes.filter(item => item.ligne.bulletin_id === ligne.bulletin_id)[0]
                if (!groupe) {
                    groupe = { key: 'p-' + ligne.bulletin_id, ligne: ligne, lignes: [], patient: true }
                    groupes.push(groupe)
                }
                groupe.lignes.push(ligne)
            })
            return groupes
        }
    },

    mounted() {
        if (this.etat) this.onglet = ETATS[this.etat] ? this.etat : 'attente'
        if (this.onglet === 'a_saisir') this.vue = 'patients'
        this.charger()
        if (typeof window !== 'undefined') this.horloge = setInterval(() => { this.maintenant = Date.now() }, 30000)
        if (typeof window !== 'undefined' && window.addEventListener) window.addEventListener('beforeunload', this.proteger_depart_navigateur)
        if (this.$router && this.$router.beforeEach) this.retirer_garde_patient = this.$router.beforeEach((to, from, next) => {
            if (!this.patient_modal || this.peut_fermer_patient()) return next()
            if (this.patient_sortie_visible) this.patient_sortie_destination = { route: to.fullPath }
            next(false)
        })
    },

    beforeDestroy() {
        this.detruit = true
        this.sequence++
        clearTimeout(this.search_timer)
        if (this.horloge) clearInterval(this.horloge)
        if (typeof window !== 'undefined' && window.removeEventListener) window.removeEventListener('beforeunload', this.proteger_depart_navigateur)
        if (this.retirer_garde_patient) this.retirer_garde_patient()
    },

    methods: {
        ouvrir_renseignement_clinique(ligne, cle) {
            if (!ligne) return
            // Lecture seule : photographie des informations du bon sélectionné, sans appel métier.
            this.renseignement_patient = {
                patient: ligne.patient, sexe: ligne.sexe, age: ligne.age,
                prescripteur: ligne.prescripteur, reference: ligne.reference,
                renseignement_clinique: ligne.renseignement_clinique
            }
            this.renseignement_retour = '#rc-open-' + this._uid + '-' + cle
            this.renseignement_visible = true
        },

        fermer_renseignement_clinique() {
            this.renseignement_visible = false
            this.renseignement_patient = null
        },

        detail_ouvert(id) {
            return !!this.details_ouverts[id]
        },

        basculer_detail(id) {
            this.$set(this.details_ouverts, id, !this.detail_ouvert(id))
        },

        charger() {
            if (this.detruit) return Promise.resolve()
            const sequence = ++this.sequence
            const data = { state: this.onglet, page: this.page, search_item: this.recherche.trim(), vue: this.vue }
            this.is_loading = true
            this.erreur = ''
            return store_laboratoire.getPaillasseListe(data).then((result) => {
                if (this.detruit || sequence !== this.sequence) return
                if (!result || !Array.isArray(result.data) || !result.counts) throw new Error('Réponse invalide')
                this.lignes = result.data
                this.details_ouverts = {}
                this.compteurs = result.counts
                this.total = result.total
                this.pages = Math.max(1, result.last_page)
                this.maintenant = Date.now()
                this.is_loading = false
            }).catch((error) => {
                if (this.detruit || sequence !== this.sequence) return
                this.lignes = []
                this.compteurs = null
                this.total = 0
                this.is_loading = false
                this.erreur = message(error, 'Impossible de charger la paillasse. Réessayez avec Actualiser.')
            })
        },

        choisir_onglet(id) {
            if (this.onglet === id) return
            this.onglet = id
            this.vue = id === 'a_saisir' ? 'patients' : 'analyses'
            this.page = 1
            this.charger()
        },

        choisir_vue(vue) {
            const suivante = vue === 'patients' ? 'patients' : 'analyses'
            if (suivante === this.vue) return
            this.vue = suivante
            this.page = 1
            this.charger()
        },

        ouvrir_patient(rangee) {
            this.patient_modal = { ...rangee, lignes: rangee.lignes.slice() }
            this.patient_fermeture_erreur = ''
            this.patient_enregistrement_message = ''
            this.patient_sortie_forcee = false
            this.patient_sortie_destination = null
            if (this.$bvModal) this.$bvModal.show('saisie-patient-paillasse')
        },

        peut_fermer_patient() {
            if (this.patient_sortie_forcee) return true
            const formulaires = this.$refs.saisiesPatient || []
            if (this.patient_enregistrement || formulaires.some(formulaire => formulaire.isBusy())) {
                this.patient_fermeture_erreur = 'Un résultat ou un document est en cours d’enregistrement. Attendez la fin avant de fermer.'
                return false
            }
            this.patient_fermeture_erreur = ''
            if (!formulaires.some(formulaire => formulaire.isDirty())) return true
            this.patient_sortie_visible = true
            return false
        },

        proteger_depart_navigateur(event) {
            if (!this.patient_modal || this.patient_sortie_forcee) return
            if (this.patient_enregistrement || (this.$refs.saisiesPatient || []).some(formulaire => formulaire.isDirty() || formulaire.isBusy())) {
                event.preventDefault()
                event.returnValue = '' // Texte natif imposé par le navigateur pour fermeture/rechargement.
            }
        },

        proteger_saisie_patient(event) {
            if (!this.peut_fermer_patient()) event.preventDefault()
        },

        creer_modele_patient(ligne) {
            if (!this.peut_fermer_patient()) { if (this.patient_sortie_visible) this.patient_sortie_destination = { modele: ligne }; return }
            this.sans_modele = ligne
            this.creer_modele()
        },

        apres_saisie_patient(ligne) {
            if (this.patient_enregistrement) return
            this.info = 'Résultat enregistré pour ' + ligne.analyse + ' (' + ligne.patient + ') : transmis à la validation biologique.'
            // Recharger la liste derrière la fenêtre, sans remplacer ses formulaires.
            return this.charger()
        },

        async enregistrer_patient(fermer = false) {
            if (this.patient_enregistrement) return false
            const formulaires = this.$refs.saisiesPatient || []
            this.patient_enregistrement_erreur = true
            if (formulaires.some(formulaire => formulaire.loading || formulaire.isBusy())) {
                this.patient_enregistrement_message = 'Attendez la fin du chargement ou du transfert des documents.'
                return false
            }
            const aEnregistrer = formulaires.filter(formulaire => formulaire.shouldSave())
            if (!aEnregistrer.length) {
                this.patient_enregistrement_erreur = false
                this.patient_enregistrement_message = 'Aucun nouveau résultat à enregistrer.'
                if (fermer) this.quitter_saisie_patient()
                return true
            }
            // Contrôler toutes les saisies avant le premier appel ; les analyses vides restent en attente.
            const invalides = aEnregistrer.filter(formulaire => !formulaire.validate())
            if (invalides.length) {
                this.patient_enregistrement_message = 'Vérifiez les champs de : ' + invalides.map(formulaire => formulaire.ligne.analyse).join(', ') + '.'
                this.patient_sortie_visible = false
                this.patient_sortie_destination = null
                return false
            }
            this.patient_enregistrement = true
            this.patient_sortie_visible = false
            this.patient_enregistrement_message = ''
            this.patient_fermeture_erreur = ''
            let enregistres = 0
            try {
                for (const formulaire of aEnregistrer) {
                    if (!await formulaire.save()) throw new Error('Échec pour « ' + formulaire.ligne.analyse + ' ».')
                    enregistres++
                }
                this.patient_enregistrement_erreur = false
                this.patient_enregistrement_message = enregistres + ' résultat(s) enregistré(s).'
            } catch (error) {
                this.patient_enregistrement_erreur = true
                this.patient_sortie_destination = null
                this.patient_enregistrement_message = enregistres + ' résultat(s) enregistré(s). ' + (error.message || 'Enregistrement interrompu.') + ' Les autres saisies sont conservées ; vous pouvez réessayer.'
            } finally {
                this.patient_enregistrement = false
                if (enregistres) this.charger()
            }
            if (fermer && !this.patient_enregistrement_erreur) this.quitter_saisie_patient()
            return !this.patient_enregistrement_erreur
        },

        continuer_saisie_patient() {
            this.patient_sortie_visible = false
            this.patient_sortie_destination = null
        },

        quitter_saisie_patient() {
            if (this.patient_enregistrement) return
            const destination = this.patient_sortie_destination
            this.patient_sortie_forcee = true
            this.patient_sortie_visible = false
            if (this.$bvModal) this.$bvModal.hide('saisie-patient-paillasse')
            if (destination && destination.modele) { this.sans_modele = destination.modele; this.creer_modele() }
            if (destination && destination.route) this.$router.push(destination.route)
        },

        fermer_patient() {
            this.patient_modal = null
            this.patient_fermeture_erreur = ''
            this.patient_sortie_forcee = false
            this.patient_sortie_destination = null
            this.patient_sortie_visible = false
        },

        chercher() {
            clearTimeout(this.search_timer)
            this.search_timer = setTimeout(() => { this.page = 1; this.charger() }, 300)
        },

        aller_page(page) {
            if (page >= 1 && page <= this.pages) { this.page = page; this.charger() }
        },

        sexe_age(ligne) {
            const sexe = sexeCourt(this, ligne && ligne.sexe)
            return ligne && ligne.age ? sexe + ' / ' + ligne.age : sexe
        },

        urgente(lignes) {
            return lignes.some(ligne => ligne.est_urgent === 1)
        },

        codes(lignes) {
            const codes = []
            lignes.forEach((ligne) => { (ligne.echantillons || []).forEach((code) => { if (codes.indexOf(code) === -1) codes.push(code) }) })
            return codes.length ? codes.join(', ') : '—'
        },

        // LAB-009 : technique — démarrer (reçues) puis terminer (en cours) ; par analyse ou pour toutes celles du patient
        a_demarrer(lignes) {
            return lignes.filter(ligne => ligne.statut === 'recu')
        },

        a_terminer(lignes) {
            return lignes.filter(ligne => ligne.statut === 'en_analyse')
        },

        occupee(lignes) {
            return lignes.some(ligne => this.en_action.indexOf(ligne.demande_id) !== -1)
        },

        demarrer(lignes, cle) {
            this.demander_confirmation_technique('demarrer', lignes, cle)
        },

        terminer(lignes, cle) {
            this.demander_confirmation_technique('terminer', lignes, cle)
        },

        demander_confirmation_technique(action, lignes, cle) {
            if (this.detruit || this.onglet !== 'attente' || this.is_loading || this.confirmation_visible || this.confirmation_en_cours || this.occupee(lignes)) return
            const selection = action === 'demarrer' ? this.a_demarrer(lignes) : this.a_terminer(lignes)
            if (!selection.length) return
            // Figer uniquement les analyses annoncées : un changement de liste ne doit pas élargir la validation.
            this.confirmation_technique = {
                action,
                patient: selection[0].patient,
                reference: selection[0].reference,
                lignes: selection.map(ligne => ({ demande_id: ligne.demande_id, bulletin_id: ligne.bulletin_id, patient: ligne.patient, analyse: ligne.analyse }))
            }
            this.confirmation_erreur = ''
            this.confirmation_retour = '#technique-' + action + '-' + this._uid + '-' + (cle || 'a-' + selection[0].demande_id)
            this.confirmation_visible = true
        },

        confirmer_technique() {
            const confirmation = this.confirmation_technique
            if (!confirmation || !this.confirmation_visible || this.confirmation_en_cours || this.detruit) return Promise.resolve()
            const statut = confirmation.action === 'demarrer' ? 'recu' : 'en_analyse'
            const selection = confirmation.lignes.map(cible => this.lignes.find(ligne => ligne.demande_id === cible.demande_id && ligne.bulletin_id === cible.bulletin_id && ligne.statut === statut))
            if (this.onglet !== 'attente' || this.is_loading || selection.some(ligne => !ligne) || this.occupee(selection)) {
                this.confirmation_erreur = 'La liste ou l’état de ces analyses a changé. Fermez cette fenêtre, actualisez la liste puis vérifiez les analyses avant de réessayer.'
                return Promise.resolve()
            }
            // Verrou posé avant le premier appel : un double clic ne lance jamais deux séries d’actions.
            this.confirmation_en_cours = true
            const action = confirmation.action === 'demarrer' ? id => store_laboratoire.debuterAnalyse(id) : id => store_laboratoire.terminerTechnique(id)
            return this.enchainer(selection, action, confirmation.action).finally(() => {
                this.confirmation_en_cours = false
                this.confirmation_visible = false
                this.confirmation_technique = null
            })
        },

        annuler_confirmation_technique() {
            if (this.confirmation_en_cours) return
            this.confirmation_visible = false
            this.confirmation_technique = null
            this.confirmation_erreur = ''
        },

        proteger_confirmation_technique(evenement) {
            if (this.confirmation_en_cours) evenement.preventDefault()
        },

        enchainer(lignes, action, quoi) {
            if (!lignes.length) return Promise.resolve()
            const ids = lignes.map(ligne => ligne.demande_id), durees = []
            this.en_action = this.en_action.concat(ids)
            this.erreur = ''
            this.info = ''
            let chaine = Promise.resolve()
            lignes.forEach((ligne) => {
                chaine = chaine.then(() => action(ligne.demande_id)).then((reponse) => {
                    if (reponse && reponse.duree_min !== undefined && reponse.duree_min !== null) durees.push(ligne.analyse + ' : ' + duree_lisible(reponse.duree_min))
                })
            })
            return chaine.then(() => {
                if (this.detruit) return
                const objet = lignes.length > 1 ? ' pour ' + lignes.length + ' analyses de ' + lignes[0].patient : ' : ' + lignes[0].analyse + ' (' + lignes[0].patient + ')'
                this.info = (quoi === 'terminer' ? 'Technique terminée' : 'Technique démarrée') + objet + (durees.length ? ' — ' + durees.join(', ') : '') + (quoi === 'terminer' ? '. Résultat(s) à saisir dans l\'onglet Saisie.' : '.')
            }).catch((error) => {
                if (this.detruit) return
                this.erreur = message(error, 'L\'action n\'a pas été enregistrée. Actualisez puis réessayez.')
            }).then(() => {
                if (this.detruit) return
                this.en_action = this.en_action.filter(id => ids.indexOf(id) === -1)
                // La liste se recharge dans tous les cas ; un refus du serveur reste affiché après le rechargement
                const refus = this.erreur
                return this.charger().then(() => { if (refus && !this.erreur && !this.detruit) this.erreur = refus })
            })
        },

        // Temps écoulé depuis le début de la technique (ligne en cours)
        depuis(debut) {
            if (!debut) return ''
            // Les dates SQL de la Clinique sont en UTC sans suffixe : ne jamais les
            // interpréter dans le fuseau du navigateur.
            const brut = String(debut).trim().replace(' ', 'T')
            const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(brut) ? brut : brut + 'Z')
            if (Number.isNaN(date.getTime())) return ''
            return duree_lisible(Math.max(0, Math.floor((this.maintenant - date.getTime()) / 60000)))
        },

        duree(minutes) {
            return duree_lisible(minutes)
        },

        // LAB-009 : parcours tracé du bon (fenêtre de suivi de la ligne)
        ouvrir_parcours(ligne) {
            this.suivi_demande = { demande_id: ligne.demande_id, nom: ligne.analyse }
            this.suivi_toggler = (this.suivi_toggler === 0) ? 1 : 0
            if (typeof $ !== 'function') return
            const modal = $('.suivi-analyse-mdl')
            if (modal && typeof modal.modal === 'function') modal.modal('show')
        },

        // Ouverture de la saisie (onglet Saisie : ligne « technique terminée ») ; une ligne encore reçue est tolérée et passe « en analyse »
        ouvrir_saisie(ligne) {
            if (this.add_result_loading) return Promise.resolve()
            this.ligne_ouverte = ligne
            this.demande_id = ligne.demande_id
            this.selected_analyse = ligne.analyse
            this.erreur = ''
            this.sans_modele = null
            this.result_meta_data = {
                patient_id: ligne.patient_id, patient_fullname: ligne.patient, patient_age: ligne.age, patient_gender: ligne.sexe, patient_phone: ligne.telephone,
                doctor_fullname: ligne.prescripteur, clinical_inquiries: ligne.renseignement_clinique, methodology_applied: ligne.technique_utilisee,
                bulletin_analyse_id: ligne.bulletin_id, demande_analyse_id: ligne.demande_id
            }
            this.$store.dispatch('setPatientMetaData', this.result_meta_data)
            this.add_result_loading = true
            this.$store.commit('SET_ADD_RESULT_LOADER', true)
            // Un refus du passage « en analyse » (ligne déjà ouverte ailleurs) n'empêche pas la saisie
            const debut = ligne.statut === 'recu' ? store_laboratoire.debuterAnalyse(ligne.demande_id).catch(() => null) : Promise.resolve(null)
            return debut.then(() => this.$store.dispatch('fetchModelesAnalyses', { analyse_id: ligne.analyse_id, status: 'active' })).then(() => {
                if (this.detruit) return
                this.$store.dispatch('setResultatsAnalyse', null)
                this.has_model = this.modeles_analyses.length > 0
                if (!this.has_model) {
                    // LAB-009 : aucun modèle de résultat : proposer de le créer plutôt qu'une fenêtre vide
                    this.terminer_chargement()
                    this.sans_modele = ligne
                    return
                }
                return this.$store.dispatch('getResultsByAnalyseIdBulletinId', { analyseId: ligne.analyse_id, bulletinId: ligne.bulletin_id }).then((response) => {
                    if (this.detruit) return
                    const existant = response && response.data && response.data.id ? response.data : null
                    if (existant) {
                        this.$store.dispatch('setResultsMode', 'edit')
                        this.$store.dispatch('setModeleAnalyse', this.modele_du_resultat(existant))
                        this.$store.dispatch('setResultatsAnalyse', existant)
                        this.terminer_chargement()
                        this.afficher_saisie()
                    } else if (this.modeles_analyses.length === 1) {
                        this.$store.dispatch('setResultsMode', 'add')
                        this.$store.dispatch('setModeleAnalyse', this.modeles_analyses[0])
                        this.terminer_chargement()
                        this.afficher_saisie()
                    } else {
                        this.$store.dispatch('setResultsMode', 'add')
                        this.terminer_chargement()
                        if (this.$bvModal) this.$bvModal.show('modeles-paillasse-' + this.demande_id)
                    }
                })
            }).catch((error) => {
                if (this.detruit) return
                this.terminer_chargement()
                this.erreur = message(error, 'La saisie n\'a pas pu être ouverte. Réessayez.')
            })
        },

        // LAB-009 : vers l'éditeur de modèles, analyse présélectionnée, retour ici après enregistrement
        creer_modele() {
            if (!this.sans_modele || !this.$router) return
            const retour = this.$route && this.$route.fullPath ? this.$route.fullPath : '/'
            this.$router.push({ name: 'creerModifierModeleAnalyse', params: { action: 'creer' }, query: { analyse_id: String(this.sans_modele.analyse_id), retour: retour } })
        },

        modele_du_resultat(existant) {
            const trouve = this.modeles_analyses.filter(modele => modele.id === existant.model_analyse_id)[0]
            return trouve || this.modeles_analyses[0]
        },

        choisir_modele(modele) {
            this.$store.dispatch('setModeleAnalyse', modele)
            this.$store.dispatch('setResultatsAnalyse', null)
            if (this.$bvModal) this.$bvModal.hide('modeles-paillasse-' + this.demande_id)
            this.afficher_saisie()
        },

        terminer_chargement() {
            this.add_result_loading = false
            this.$store.commit('SET_ADD_RESULT_LOADER', false)
        },

        afficher_saisie() {
            if (typeof $ !== 'function') return
            const modal = $('#saisie-resultats-analyse-modal-' + this.demande_id)
            if (modal && typeof modal.modal === 'function') modal.modal('show')
        },

        // Après enregistrement : la Clinique a marqué la ligne « résultat saisi » ; elle part en validation biologique
        apres_saisie() {
            const ligne = this.ligne_ouverte
            this.info = ligne ? 'Résultat enregistré pour ' + ligne.analyse + ' (' + ligne.patient + ') : transmis à la validation biologique.' : 'Résultat enregistré : transmis à la validation biologique.'
            return this.charger()
        },

        date_heure(value) {
            if (!value) return 'Non renseignée'
            const config = this.$store && this.$store.getters && this.$store.getters.config_info_clinique
            const zone = (config && config.fuseau_horaire) ||
                (typeof this.getClinicTimezone === 'function' ? this.getClinicTimezone() : Intl.DateTimeFormat().resolvedOptions().timeZone)
            return date_heure_zone(value, zone || 'UTC', 'UTC')
        }
    }
}
